/**
 * Admin €Punkte-Verwaltung.
 *
 * Tab 1: Regeln (CRUD: Punkte, €-Wert, aktiv/inaktiv) — gruppiert nach Bereich
 * Tab 2: Benutzer-Salden (Liste mit Salden, Klick → Detail-Ledger + manuelle Buchung)
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";
import { previewPdfResponse } from "../lib/pdfPreview";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Loader2, Shield, Save, Plus, Minus, X, FileText, Send, Download, Play } from "lucide-react";
import { toast } from "sonner";

function Pill({ children, tone = "default" }) {
    const cls = {
        default: "bg-gray-100 text-gray-700",
        accent: "bg-[#FFCC00]/20 text-[#111827]",
        neg: "bg-red-100 text-red-700",
        pos: "bg-green-100 text-green-700",
    }[tone] || "bg-gray-100";
    return <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${cls}`}>{children}</span>;
}

function eur(v) {
    return (v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---------------- Regeln-Tab ------------------------------------------------

function RulesTab() {
    const [rules, setRules] = useState([]);
    const [bereichLabels, setBereichLabels] = useState({});
    const [loading, setLoading] = useState(true);
    const [dirty, setDirty] = useState({});  // { ruleId: { points, euro_value, active } }
    const [savingId, setSavingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/points/rules");
            setRules(data.items || []);
            setBereichLabels(data.bereich_labels || {});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler beim Laden");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const grouped = useMemo(() => {
        const out = {};
        for (const r of rules) {
            const b = r.bereich || "manuell";
            (out[b] = out[b] || []).push(r);
        }
        return out;
    }, [rules]);

    const setField = (id, field, value) => {
        setDirty((d) => ({ ...d, [id]: { ...(d[id] || {}), [field]: value } }));
    };

    const saveRule = async (rule) => {
        const patch = dirty[rule.id];
        if (!patch) return;
        setSavingId(rule.id);
        try {
            await api.patch(`/admin/points/rules/${rule.id}`, patch);
            toast.success("Regel gespeichert");
            setDirty((d) => { const x = { ...d }; delete x[rule.id]; return x; });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSavingId(null);
        }
    };

    if (loading) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</div>;

    return (
        <div className="space-y-6" data-testid="admin-points-rules">
            <p className="text-sm text-gray-600">
                Hier können Sie für jede getrackte Aktion <strong>€Punkte</strong> und den zugeordneten <strong>€-Wert</strong> festlegen.
                Änderungen wirken sich ab der nächsten Buchung aus (rückwirkend werden Ledger-Einträge nicht verändert).
            </p>
            {Object.entries(grouped).map(([bereich, items]) => (
                <div key={bereich} className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="font-display font-bold text-base">{bereichLabels[bereich] || bereich}</h3>
                        <Pill tone="accent">{items.length} Regel{items.length === 1 ? "" : "n"}</Pill>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50/60 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                            <tr>
                                <th className="text-left p-2 pl-5">Aktion</th>
                                <th className="text-right p-2 w-28">€Punkte</th>
                                <th className="text-right p-2 w-32">€-Wert</th>
                                <th className="text-center p-2 w-20">Aktiv</th>
                                <th className="p-2 w-24"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => {
                                const d = dirty[r.id] || {};
                                const p = d.points ?? r.points;
                                const e = d.euro_value ?? r.euro_value;
                                const a = d.active ?? r.active;
                                const isDirty = !!dirty[r.id];
                                return (
                                    <tr key={r.id} className="border-t border-gray-100" data-testid={`rule-row-${r.action_key}`}>
                                        <td className="p-2 pl-5">
                                            <div className="font-bold text-[#111827]">{r.label}</div>
                                            <div className="font-mono text-[10px] text-gray-500">{r.action_key}</div>
                                        </td>
                                        <td className="p-2">
                                            <Input
                                                type="number"
                                                value={p}
                                                onChange={(ev) => setField(r.id, "points", parseInt(ev.target.value || "0", 10))}
                                                className="rounded-sm text-right font-mono-num text-sm h-8"
                                                data-testid={`rule-points-${r.action_key}`}
                                            />
                                        </td>
                                        <td className="p-2">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={e}
                                                onChange={(ev) => setField(r.id, "euro_value", parseFloat(ev.target.value || "0"))}
                                                className="rounded-sm text-right font-mono-num text-sm h-8"
                                                data-testid={`rule-euro-${r.action_key}`}
                                            />
                                        </td>
                                        <td className="p-2 text-center">
                                            <input
                                                type="checkbox"
                                                checked={!!a}
                                                onChange={(ev) => setField(r.id, "active", ev.target.checked)}
                                                className="w-4 h-4 accent-[#FFCC00]"
                                                data-testid={`rule-active-${r.action_key}`}
                                            />
                                        </td>
                                        <td className="p-2 text-right pr-5">
                                            <Button
                                                onClick={() => saveRule(r)}
                                                disabled={!isDirty || savingId === r.id}
                                                className="bg-[#111827] text-white hover:bg-[#FFCC00] hover:text-[#111827] rounded-sm h-8 text-xs font-bold uppercase tracking-wider px-3"
                                                data-testid={`rule-save-${r.action_key}`}
                                            >
                                                {savingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
}

// ---------------- Benutzer-Tab ----------------------------------------------

function UsersTab() {
    const [items, setItems] = useState([]);
    const [period, setPeriod] = useState("");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const url = period ? `/admin/points/users?period=${period}` : "/admin/points/users";
            const { data } = await api.get(url);
            setItems(data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler beim Laden");
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => { load(); }, [load]);

    if (selected) {
        return <UserDetail userId={selected} period={period} onClose={() => { setSelected(null); load(); }} />;
    }

    return (
        <div className="space-y-4" data-testid="admin-points-users">
            <div className="flex items-center gap-3 flex-wrap">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Zeitraum:</label>
                <Select value={period || "all"} onValueChange={(v) => setPeriod(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[180px] rounded-sm h-8" data-testid="users-period-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Gesamter Zeitraum</SelectItem>
                        {(() => {
                            const out = [];
                            const now = new Date();
                            for (let i = 0; i < 12; i++) {
                                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                                const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                                out.push(<SelectItem key={k} value={k}>{k}</SelectItem>);
                            }
                            return out;
                        })()}
                    </SelectContent>
                </Select>
            </div>
            {loading ? (
                <div className="text-sm text-gray-500"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Lädt…</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-sm p-6 text-center">
                    Keine Punkte-Buchungen im gewählten Zeitraum.
                </div>
            ) : (
                <table className="w-full bg-white border border-gray-200 rounded-sm overflow-hidden text-sm">
                    <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                        <tr>
                            <th className="text-left p-2 pl-4">Benutzer</th>
                            <th className="text-right p-2">€Punkte</th>
                            <th className="text-right p-2">€-Wert</th>
                            <th className="text-right p-2">Ereignisse</th>
                            <th className="text-right p-2 pr-4">Letztes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((u) => (
                            <tr
                                key={u.user_id}
                                onClick={() => setSelected(u.user_id)}
                                className="border-t border-gray-100 cursor-pointer hover:bg-[#FFCC00]/5"
                                data-testid={`user-row-${u.user_id}`}
                            >
                                <td className="p-2 pl-4">
                                    <div className="font-bold text-[#111827]">{u.name}</div>
                                    <div className="text-[11px] text-gray-500">{u.email} · {u.role}</div>
                                </td>
                                <td className="p-2 text-right">
                                    <Pill tone={u.points >= 0 ? "pos" : "neg"}>{u.points >= 0 ? "+" : ""}{u.points} €Pkt</Pill>
                                </td>
                                <td className="p-2 text-right font-mono-num font-bold">
                                    <span className={u.euro >= 0 ? "text-green-700" : "text-red-700"}>
                                        {u.euro >= 0 ? "+" : ""}{eur(u.euro)} €
                                    </span>
                                </td>
                                <td className="p-2 text-right font-mono-num text-gray-700">{u.events}</td>
                                <td className="p-2 pr-4 text-right text-[11px] text-gray-500 font-mono-num">
                                    {u.last_at ? new Date(u.last_at).toLocaleDateString("de-DE") : "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ---------------- User Detail + Adjust --------------------------------------

function UserDetail({ userId, period, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [adjustOpen, setAdjustOpen] = useState(false);
    const [adj, setAdj] = useState({ points: 0, euro_value: 0, reason: "", bereich: "manuell" });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const url = period ? `/admin/points/users/${userId}?period=${period}` : `/admin/points/users/${userId}`;
            const { data } = await api.get(url);
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler beim Laden");
        } finally {
            setLoading(false);
        }
    }, [userId, period]);

    useEffect(() => { load(); }, [load]);

    const submitAdjust = async () => {
        if (!adj.reason || adj.reason.length < 3) {
            toast.error("Begründung ist erforderlich (mind. 3 Zeichen)");
            return;
        }
        if (adj.points === 0 && adj.euro_value === 0) {
            toast.error("Punkte und/oder €-Wert müssen ungleich 0 sein");
            return;
        }
        try {
            await api.post(`/admin/points/users/${userId}/adjust`, adj);
            toast.success("Buchung erfasst");
            setAdjustOpen(false);
            setAdj({ points: 0, euro_value: 0, reason: "", bereich: "manuell" });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen");
        }
    };

    if (loading || !data) return <div className="text-sm text-gray-500"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Lädt…</div>;

    const { user, balance, entries } = data;

    return (
        <div className="space-y-4" data-testid={`user-detail-${userId}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <button onClick={onClose} className="text-xs text-gray-500 hover:text-[#111827] mb-1 inline-flex items-center gap-1" data-testid="user-detail-back">
                        <X className="w-3 h-3" /> Zurück zur Liste
                    </button>
                    <h2 className="font-display font-bold text-2xl">{user.company_name || user.name || user.email}</h2>
                    <div className="text-xs text-gray-500">{user.email} · {user.role} · ID: <span className="font-mono">{user.id}</span></div>
                </div>
                <Button
                    onClick={() => setAdjustOpen(true)}
                    className="bg-[#FFCC00] text-[#111827] hover:bg-[#111827] hover:text-[#FFCC00] rounded-sm font-bold uppercase tracking-wider text-xs"
                    data-testid="open-adjust-btn"
                >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Manuelle Buchung
                </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-[#FFCC00]/10 border border-[#FFCC00]/40 rounded-sm p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Saldo €Punkte</div>
                    <div className="font-display font-black text-2xl font-mono-num">{balance.total.points}</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-sm p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">€-Wert</div>
                    <div className="font-display font-black text-2xl font-mono-num">{eur(balance.total.euro)} €</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Buchungen</div>
                    <div className="font-display font-black text-2xl font-mono-num">{balance.total.count}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Bereiche</div>
                    <div className="font-display font-black text-2xl font-mono-num">{Object.keys(balance.by_bereich).length}</div>
                </div>
            </div>

            {Object.keys(balance.by_bereich).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                    <div className="px-5 py-2 bg-gray-50 border-b text-xs font-bold uppercase tracking-wider text-gray-600">Aufschlüsselung nach Bereich</div>
                    <table className="w-full text-sm">
                        <tbody>
                            {Object.entries(balance.by_bereich).map(([bk, b]) => (
                                <tr key={bk} className="border-t border-gray-100">
                                    <td className="p-2 pl-5 font-bold">{b.label}</td>
                                    <td className="p-2 text-right font-mono-num">{b.points} €Pkt</td>
                                    <td className="p-2 text-right font-mono-num">{eur(b.euro)} €</td>
                                    <td className="p-2 pr-5 text-right text-gray-500 text-xs">{b.count} Buch.</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                <div className="px-5 py-2 bg-gray-50 border-b text-xs font-bold uppercase tracking-wider text-gray-600">Ledger ({entries.length})</div>
                {entries.length === 0 ? (
                    <div className="p-6 text-sm text-gray-500 text-center">Noch keine Buchungen.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50/50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                            <tr>
                                <th className="text-left p-2 pl-5">Datum</th>
                                <th className="text-left p-2">Aktion</th>
                                <th className="text-left p-2">Bereich</th>
                                <th className="text-right p-2">€Punkte</th>
                                <th className="text-right p-2 pr-5">€-Wert</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((e) => (
                                <tr key={e.id} className="border-t border-gray-100" data-testid={`ledger-row-${e.id}`}>
                                    <td className="p-2 pl-5 font-mono-num text-xs text-gray-600">{new Date(e.created_at).toLocaleString("de-DE")}</td>
                                    <td className="p-2">
                                        <div className="font-bold">{e.label}</div>
                                        {e.reason && <div className="text-[11px] text-gray-500 italic">{e.reason}</div>}
                                    </td>
                                    <td className="p-2"><Pill>{e.bereich}</Pill></td>
                                    <td className="p-2 text-right font-mono-num font-bold">
                                        <span className={e.points >= 0 ? "text-green-700" : "text-red-700"}>{e.points >= 0 ? "+" : ""}{e.points}</span>
                                    </td>
                                    <td className="p-2 pr-5 text-right font-mono-num">
                                        <span className={e.euro_value >= 0 ? "text-green-700" : "text-red-700"}>{e.euro_value >= 0 ? "+" : ""}{eur(e.euro_value)} €</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {adjustOpen && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="adjust-modal">
                    <div className="bg-white border-l-4 border-[#FFCC00] rounded-sm w-full max-w-md p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="font-display font-bold text-xl">Manuelle Buchung</h3>
                            <button onClick={() => setAdjustOpen(false)} className="text-gray-500 hover:text-[#111827]"><X className="w-4 h-4" /></button>
                        </div>
                        <p className="text-xs text-gray-600">Für {user.company_name || user.name}. Positive Werte = Gutschrift, negative = Abzug (z.B. inaktives Marketing).</p>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-600">€Punkte</label>
                            <Input
                                type="number"
                                value={adj.points}
                                onChange={(e) => setAdj({ ...adj, points: parseInt(e.target.value || "0", 10) })}
                                className="rounded-sm font-mono-num"
                                data-testid="adjust-points"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-600">€-Wert</label>
                            <Input
                                type="number"
                                step="0.01"
                                value={adj.euro_value}
                                onChange={(e) => setAdj({ ...adj, euro_value: parseFloat(e.target.value || "0") })}
                                className="rounded-sm font-mono-num"
                                data-testid="adjust-euro"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Bereich</label>
                            <Select value={adj.bereich} onValueChange={(v) => setAdj({ ...adj, bereich: v })}>
                                <SelectTrigger className="rounded-sm" data-testid="adjust-bereich"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="stammdaten">Stammdaten</SelectItem>
                                    <SelectItem value="marktplatz">Marktplatz</SelectItem>
                                    <SelectItem value="marketing">Marketing</SelectItem>
                                    <SelectItem value="inserate">Inserate</SelectItem>
                                    <SelectItem value="engagement">Engagement</SelectItem>
                                    <SelectItem value="tarif">Tarif & Abrechnung</SelectItem>
                                    <SelectItem value="compliance">Compliance</SelectItem>
                                    <SelectItem value="manuell">Manuelle Buchungen</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Begründung</label>
                            <Input
                                value={adj.reason}
                                onChange={(e) => setAdj({ ...adj, reason: e.target.value })}
                                placeholder="z.B. Inaktives Marketing im Monat Mai"
                                className="rounded-sm"
                                data-testid="adjust-reason"
                            />
                        </div>
                        <div className="flex gap-2 pt-2">
                            <Button
                                onClick={submitAdjust}
                                className="flex-1 bg-[#111827] text-white hover:bg-[#FFCC00] hover:text-[#111827] rounded-sm font-bold uppercase tracking-wider"
                                data-testid="adjust-submit"
                            >
                                {adj.points < 0 || adj.euro_value < 0 ? <Minus className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                                Buchung erfassen
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------- Billing-Tab -----------------------------------------------

function BillingTab() {
    const [items, setItems] = useState([]);
    const [cronRuns, setCronRuns] = useState([]);
    const [period, setPeriod] = useState(() => {
        const n = new Date();
        return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
    });
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);
    const [cronRunning, setCronRunning] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [invs, runs] = await Promise.all([
                api.get(`/admin/billing/invoices?period=${period}`),
                api.get("/admin/billing/cron-runs"),
            ]);
            setItems(invs.data.items || []);
            setCronRuns(runs.data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler beim Laden");
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => { load(); }, [load]);

    const runBilling = async () => {
        setRunning(true);
        try {
            const { data } = await api.post(`/admin/billing/run?period=${period}`);
            toast.success(`${data.generated_count} Rechnungen erzeugt`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Lauf fehlgeschlagen");
        } finally {
            setRunning(false);
        }
    };

    const runCron = async () => {
        if (!confirm("Manueller Cron-Lauf für den Vormonat starten? Dies generiert + versendet alle Rechnungen automatisch. Lauf ist nur einmal pro Periode möglich.")) return;
        setCronRunning(true);
        try {
            const { data } = await api.post("/admin/billing/run-cron");
            if (data.skipped) {
                toast.info(`Bereits durchgeführt: ${data.previous_run?.period}`);
            } else {
                toast.success(`Cron-Lauf ${data.period}: ${data.generated} Rechnung(en) erzeugt — Versand erst nach Freigabe („Senden" je Rechnung)`);
            }
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cron fehlgeschlagen");
        } finally {
            setCronRunning(false);
        }
    };

    const dispatchInvoice = async (inv) => {
        setBusyId(inv.id);
        try {
            await api.post(`/admin/billing/invoices/${inv.id}/dispatch`);
            toast.success("Rechnung versandt (MOCKED – Resend-Key fehlt noch)");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
        } finally {
            setBusyId(null);
        }
    };

    const downloadPdf = async (inv) => {
        try {
            const res = await api.get(`/admin/billing/invoices/${inv.id}/pdf`, { responseType: "blob" });
            previewPdfResponse(res, (inv.invoice_number || inv.id) + ".pdf");
        } catch (e) {
            toast.error("PDF-Download fehlgeschlagen");
        }
    };

    const nonZero = items.filter((i) => i.net_amount !== 0);
    const totalNet = items.reduce((s, i) => s + (i.net_amount || 0), 0);

    return (
        <div className="space-y-4" data-testid="admin-billing">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Zeitraum:</label>
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger className="w-[180px] rounded-sm h-8" data-testid="billing-period-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {(() => {
                                const out = [];
                                const now = new Date();
                                for (let i = 0; i < 12; i++) {
                                    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                                    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                                    out.push(<SelectItem key={k} value={k}>{k}</SelectItem>);
                                }
                                return out;
                            })()}
                        </SelectContent>
                    </Select>
                </div>
                <Button
                    onClick={runBilling}
                    disabled={running}
                    className="bg-[#FFCC00] text-[#111827] hover:bg-[#111827] hover:text-[#FFCC00] rounded-sm font-bold uppercase tracking-wider text-xs"
                    data-testid="billing-run-btn"
                >
                    {running ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                    Abrechnungslauf starten
                </Button>
                <Button
                    onClick={runCron}
                    disabled={cronRunning}
                    className="bg-[#111827] text-white hover:bg-[#FFCC00] hover:text-[#111827] rounded-sm font-bold uppercase tracking-wider text-xs"
                    data-testid="cron-run-btn"
                    title="Generiert + versendet automatisch — wie der echte Cron am 1. des Monats"
                >
                    {cronRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                    Cron-Lauf (Vormonat)
                </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white border border-gray-200 rounded-sm p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Rechnungen</div>
                    <div className="font-display font-black text-2xl font-mono-num">{items.length}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Davon ≠ 0 €</div>
                    <div className="font-display font-black text-2xl font-mono-num">{nonZero.length}</div>
                </div>
                <div className="bg-[#FFCC00]/10 border border-[#FFCC00]/40 rounded-sm p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Summe Netto</div>
                    <div className="font-display font-black text-2xl font-mono-num">{eur(totalNet)} €</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Versandt</div>
                    <div className="font-display font-black text-2xl font-mono-num">{items.filter((i) => i.status === "dispatched").length}</div>
                </div>
            </div>
            {cronRuns && cronRuns.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-4" data-testid="cron-history">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-600">Cron-Historie · Monatsabschluss</div>
                        <span className="text-[10px] text-gray-500">Auto-Lauf am 1. des Monats, 04:00–08:00 UTC</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {cronRuns.slice(0, 12).map((r) => (
                            <span key={r.key} className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm border text-[11px] ${r.failed > 0 ? "bg-red-50 border-red-200 text-red-800" : "bg-white border-gray-200 text-gray-800"}`} data-testid={`cron-run-${r.period}`}>
                                <span className="font-mono-num font-bold">{r.period}</span>
                                <span className="text-gray-500">·</span>
                                <span>{r.generated} gen.</span>
                                <span className="text-gray-500">·</span>
                                <span>{r.dispatched} versandt</span>
                                {r.manual && <span className="ml-1 px-1 bg-[#FFCC00] text-[#111827] text-[9px] font-bold uppercase rounded">manuell</span>}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-sm text-gray-500"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Lädt…</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-sm p-6 text-center">
                    Noch keine Rechnungen für {period}. Klicken Sie "Abrechnungslauf starten".
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                            <tr>
                                <th className="text-left p-2 pl-4">Rechnung</th>
                                <th className="text-left p-2">Händler</th>
                                <th className="text-right p-2">Grundpreis</th>
                                <th className="text-right p-2">€Punkte</th>
                                <th className="text-right p-2">Netto</th>
                                <th className="text-center p-2">Status</th>
                                <th className="text-right p-2 pr-4">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...items].sort((a, b) => Math.abs(b.net_amount) - Math.abs(a.net_amount)).map((inv) => (
                                <tr key={inv.id} className="border-t border-gray-100" data-testid={`invoice-row-${inv.id}`}>
                                    <td className="p-2 pl-4 font-mono text-[11px]">{inv.invoice_number}</td>
                                    <td className="p-2">
                                        <div className="font-bold">{inv.user_name}</div>
                                        <div className="text-[11px] text-gray-500">{inv.user_email}</div>
                                    </td>
                                    <td className="p-2 text-right font-mono-num">{eur(inv.base_amount)} €</td>
                                    <td className="p-2 text-right font-mono-num">
                                        <span className={inv.points_credit_euro >= 0 ? "text-green-700" : "text-red-700"}>
                                            {inv.points_credit_euro >= 0 ? "−" : "+"}{eur(Math.abs(inv.points_credit_euro))} €
                                        </span>
                                    </td>
                                    <td className="p-2 text-right font-mono-num font-bold">{eur(inv.net_amount)} €</td>
                                    <td className="p-2 text-center">
                                        <Pill tone={inv.status === "dispatched" ? "pos" : "default"}>{inv.status}</Pill>
                                    </td>
                                    <td className="p-2 pr-4 text-right">
                                        <div className="inline-flex gap-1">
                                            <Button
                                                onClick={() => downloadPdf(inv)}
                                                className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-sm h-7 text-[10px] font-bold uppercase tracking-wider px-2"
                                                data-testid={`invoice-pdf-${inv.id}`}
                                            >
                                                <Download className="w-3 h-3 mr-1" /> PDF
                                            </Button>
                                            <Button
                                                onClick={() => dispatchInvoice(inv)}
                                                disabled={busyId === inv.id || inv.status === "dispatched"}
                                                className="bg-[#111827] text-white hover:bg-[#FFCC00] hover:text-[#111827] rounded-sm h-7 text-[10px] font-bold uppercase tracking-wider px-2"
                                                data-testid={`invoice-dispatch-${inv.id}`}
                                            >
                                                {busyId === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                                                {inv.status === "dispatched" ? "✓" : "Senden"}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ---------------- Page ------------------------------------------------------

export default function AdminPointsPage() {
    const { user, loading } = useAuth();

    if (loading) return <div className="p-8 text-sm text-gray-500">Lädt…</div>;
    if (!user || user.role !== "admin") {
        return (
            <div className="max-w-3xl mx-auto px-4 py-12 text-center" data-testid="admin-points-forbidden">
                <Shield className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h1 className="font-display text-2xl font-bold">Zugriff verweigert</h1>
                <p className="text-sm text-gray-600 mt-2">Nur Admins können diese Seite öffnen.</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="admin-points-page">
            <div className="mb-6">
                <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] mb-1">Admin</div>
                <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">€Punkte-Verwaltung</h1>
                <p className="text-gray-600 mt-2 text-sm">
                    Konfigurieren Sie Punkte- und €-Werte pro Aktion und werten Sie Benutzersalden für die monatliche Tarif-Abrechnung aus.
                </p>
            </div>
            <Tabs defaultValue="rules" className="w-full">
                <TabsList className="bg-white border border-gray-200 rounded-sm p-1">
                    <TabsTrigger value="rules" data-testid="tab-rules">Regeln</TabsTrigger>
                    <TabsTrigger value="users" data-testid="tab-users">Benutzer-Salden</TabsTrigger>
                    <TabsTrigger value="billing" data-testid="tab-billing"><FileText className="w-3.5 h-3.5 mr-1.5" /> Monatsabrechnung</TabsTrigger>
                </TabsList>
                <TabsContent value="rules" className="mt-5">
                    <RulesTab />
                </TabsContent>
                <TabsContent value="users" className="mt-5">
                    <UsersTab />
                </TabsContent>
                <TabsContent value="billing" className="mt-5">
                    <BillingTab />
                </TabsContent>
            </Tabs>
        </div>
    );
}
